import { useCreateConversationAction } from "@/action/use-create-conversation-action";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { getInitialChatName } from "@/lib/chat-name";
import { getFirstName } from "@/lib/string";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useAuthDetails } from "../../context/auth-context";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { AIChatInput } from "../AIChatInput/AIChatInput";
import { useAssistContext } from "../Assist/AssistContext";
import { SingleExpertSkillsSection } from "../Assist/skill/SingleExpertSkillsSection";
import { useExpertSelection } from "../Expert/selection/ExpertSelectionContext";
import { ExpertSelectionRow } from "../Expert/selection/ExpertSelectionRow";
import { GroupExpertCarousel } from "../Expert/selection/GroupExpertCarousel";
import { ChatFileManager } from "../file/ChatFileManager/ChatFileManager";
import { ChatFileManagerAttachmentButton } from "../file/ChatFileManager/ChatFileManagerAttachmentButton";
import { useChatFileManager } from "../file/ChatFileManager/ChatFileManagerContext";
import { Column } from "../ui/flex";

export const HomeChat: React.FC = () => {
  const navigate = useNavigate();
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const authDetails = useAuthDetails();
  const { selectedExpertUuids } = useExpertSelection();
  const { create: createConversation, isCreatingConversation } =
    useCreateConversationAction();
  const {
    capabilities,
    setCapabilities,
    modelProfileUuid,
    setModelProfileUuid,
  } = useAssistContext();
  const [prompt, setPrompt] = useState<string>("");
  const [folderUuids, setFolderUuids] = useState<string[]>([]);
  const { selectedLocalFiles, selectedExternalFiles } = useChatFileManager();

  const firstName = authDetails ? getFirstName(authDetails.name) : "User";

  const handleStartConversation = async (
    prompt: string,
    _: string | null,
    folderUuids: string[],
    skillUuid?: string,
  ) => {
    if (
      (!prompt.trim() && !skillUuid) ||
      !selectedExpertUuids.size ||
      isCreatingConversation
    ) {
      return;
    }

    try {
      const { kitUuid, chatUuid } = await createConversation({
        name: getInitialChatName(),
        expertUuids: Array.from(selectedExpertUuids),
        prompt,
        folderUuids,
        localFiles: selectedLocalFiles,
        externalFiles: selectedExternalFiles,
        skillUuid,
      });

      navigate({
        to: "/w/$groupSlug/k/$kitUuid/chat/$chatUuid",
        params: { groupSlug, kitUuid, chatUuid },
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to create chat");
    }
  };

  const filePickerElement = groupUuid ? (
    <>
      <ChatFileManagerAttachmentButton
        library={false}
        disabled={isCreatingConversation}
      />
    </>
  ) : undefined;

  return (
    <Column className="w-full min-w-[400px] gap-4">
      <Column className="gap-2">
        <h1 className="font-family-fancy text-4xl font-normal tracking-tighter">
          Welcome back, <span className="font-bold">{firstName}</span>
          <span className="text-accent">.</span>
        </h1>
        <span className="text-accent text-sm font-bold uppercase">
          Choose an Expert AI to discuss today's marketing objective.
        </span>
      </Column>
      <GroupExpertCarousel />
      <ExpertSelectionRow />
      {/* ChatFileManagerProvider: ui/src/pages/GroupHomePage.tsx */}
      <ChatFileManager goal="select" />
      <AIChatInput
        onSubmit={handleStartConversation}
        onChange={setPrompt}
        value={prompt}
        isPending={isCreatingConversation}
        placeholder="Ask them anything about your marketing objective"
        filePickerElement={filePickerElement}
        enableCommand={true}
        foldersGroupUuid={groupUuid}
        capabilities={capabilities}
        onCapabilitiesChange={setCapabilities}
        modelProfileUuid={modelProfileUuid}
        onModelProfileUuidChange={setModelProfileUuid}
        onSelectFolder={setFolderUuids}
        expectExpertSelection
      />
      <div className="absolute bottom-0 left-0 translate-y-full">
        {selectedExpertUuids.size === 1 && (
          <SingleExpertSkillsSection
            key={selectedExpertUuids.values().next().value}
            expertUuid={selectedExpertUuids.values().next().value!}
            onSelect={(params) =>
              handleStartConversation(
                prompt,
                null,
                folderUuids,
                params.skillUuid,
              )
            }
          />
        )}
      </div>
    </Column>
  );
};
